/**
 * /manage/members — org members + pending invitations.
 *
 * Mockup `Manage > Members` UI (members roster, role badges, role legend) wired
 * to the real admin API the standalone console used: list/remove members, list/
 * invite/revoke invitations. Role is shown as a read-only badge — the platform
 * has no inline role-change endpoint, so the mockup's role <select> is omitted
 * rather than faked.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Copy, Mail, Search, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { initials } from "@/components/settings/settingsKit";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

import { useApi } from "@/api";
import { useActiveOrgId, useIsDemoManage } from "@/components/manage/org-context";
import { useDocumentTitle } from "@/lib/document-title";

export const Route = createFileRoute("/manage/members")({
  component: MembersPage,
});

interface MemberRow {
  user_id: string;
  org_id: string;
  role: string;
  name: string | null;
  email: string | null;
  joined_at: string;
}

interface InvitationRow {
  id: string;
  org_id: string;
  email: string;
  role: string;
  token: string;
  status: "pending" | "accepted" | "revoked" | "expired";
  expires_at: string | null;
  created_at: string;
  accepted_at: string | null;
}

const WEB_APP_URL =
  (import.meta as { env?: { VITE_WEB_APP_URL?: string } }).env?.VITE_WEB_APP_URL ??
  window.location.origin;

const ROLE_DESC: Record<string, string> = {
  org_owner:
    "Full access everywhere — owns every space, plus billing and deleting the organization.",
  org_admin:
    "Create workspaces, projects, and objectives, and manage membership on any space. No automatic access to content unless granted.",
  org_member:
    "No access by default — granted per-space. Becomes owner of anything they create.",
};

/** Display label for an org role badge — the prefixed enum values map to a
 * human label, while the badge CSS class still keys on the base name. */
const ROLE_LABEL: Record<string, string> = {
  org_owner: "Org Owner",
  org_admin: "Org Admin",
  org_member: "Org Member",
};

/** Map the prefixed org role to the base badge CSS class (`.badge.owner` etc.)
 * that plan-skin.css already styles, so `badge org_*` resolves to a color. */
const ROLE_BADGE_CLASS: Record<string, string> = {
  org_owner: "owner",
  org_admin: "admin",
  org_member: "member",
};

function MembersPage() {
  useDocumentTitle(["Members", "Manage"]);
  const api = useApi();
  const orgId = useActiveOrgId();
  // Demo sessions browse Manage read-only: no invites, no removals, and the
  // invitations endpoint is demo-403'd, so don't even ask for it.
  const isDemo = useIsDemoManage();
  const qc = useQueryClient();
  const [q, setQ] = useState("");

  const members = useQuery({
    queryKey: ["admin-members", orgId],
    queryFn: () => api.request<MemberRow[]>(`/admin/orgs/${orgId}/members`),
  });
  const invitations = useQuery({
    queryKey: ["admin-invitations", orgId],
    queryFn: () => api.request<InvitationRow[]>(`/admin/orgs/${orgId}/invitations`),
    enabled: !isDemo,
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) =>
      api.request<void>(`/admin/orgs/${orgId}/members/${userId}`, { method: "DELETE" }, false),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-members", orgId] });
      toast.success("User removed from org");
    },
    onError: (err: { message?: string }) => toast.error(err.message ?? "Failed to remove user"),
  });
  const revokeInvite = useMutation({
    mutationFn: (id: string) =>
      api.request<void>(`/admin/invitations/${id}`, { method: "DELETE" }, false),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-invitations", orgId] });
      toast.success("Invitation revoked");
    },
  });

  const needle = q.trim().toLowerCase();
  const shown = (members.data ?? []).filter(
    (m) =>
      !needle ||
      (m.name ?? "").toLowerCase().includes(needle) ||
      (m.email ?? "").toLowerCase().includes(needle),
  );
  const pending = (invitations.data ?? []).filter((i) => i.status === "pending");
  const roles = ["org_owner", "org_admin", "org_member"];

  return (
    <div className="plan-skin">
      <div className="mx-auto max-w-[920px] px-10 py-9">
        <div className="manage-head">
          <div className="crumb">MANAGE</div>
          <h1 className="page">Members</h1>
          <p className="page-sub">Manage who has access to this organization.</p>
        </div>
        <hr className="rule" />

        {/* data-tour anchor: the demo guided tour spotlights the members
            roster (search + list — always mounted, even while loading). */}
        <div className="manage-body" data-tour="manage-members">
          <div className="members-bar">
            <div className="searchbar" style={{ margin: 0, flex: 1, maxWidth: "none" }}>
              <Search size={16} className="search-ico" />
              <input
                className="search-input"
                placeholder="Search members…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            {!isDemo && <InviteUserDialog orgId={orgId} />}
          </div>

          {members.isLoading ? (
            <Skeleton className="h-40 w-full rounded-lg" />
          ) : (
            <div className="members-list">
              <div className="members-head">
                <div>Member</div>
                <div>Role</div>
                <div>Joined</div>
                <div />
              </div>
              {shown.length === 0 ? (
                <div className="member-row" style={{ color: "var(--ink-faint)" }}>
                  {needle ? "No members match." : "No members yet."}
                </div>
              ) : (
                shown.map((m) => (
                  <div className="member-row" key={m.user_id}>
                    <div className="member-id">
                      <span className="member-avatar">{initials(m.name || m.email)}</span>
                      <div>
                        <div className="member-name">{m.name || (m.email ?? "—")}</div>
                        <div className="member-email">{m.email ?? "—"}</div>
                      </div>
                    </div>
                    <div>
                      <span className={`badge ${ROLE_BADGE_CLASS[m.role] ?? m.role}`}>
                        {ROLE_LABEL[m.role] ?? m.role}
                      </span>
                    </div>
                    <div className="member-active">{relTime(m.joined_at)}</div>
                    <div style={{ textAlign: "right" }}>
                      {!isDemo && m.role !== "owner" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 text-muted-foreground"
                          aria-label="Remove member"
                          onClick={() => {
                            if (confirm(`Remove ${m.email} from this org?`))
                              removeMember.mutate(m.user_id);
                          }}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {pending.length > 0 && (
            <div className="settings-section">
              <div className="settings-section-h">Pending invitations</div>
              <div className="members-list">
                {pending.map((inv) => {
                  const url = `${WEB_APP_URL}/invite/${inv.token}`;
                  return (
                    <div className="member-row" key={inv.id}>
                      <div className="member-id">
                        <span className="member-avatar">
                          <Mail className="size-3.5" />
                        </span>
                        <div>
                          <div className="member-name">{inv.email}</div>
                          <div className="member-email">Invited · {inv.role}</div>
                        </div>
                      </div>
                      <div>
                        <span className={`badge ${inv.status}`}>{inv.status}</span>
                      </div>
                      <div className="member-active">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 gap-1.5 px-2 text-xs"
                          onClick={() => {
                            void navigator.clipboard.writeText(url);
                            toast.success("Invite link copied");
                          }}
                        >
                          <Copy className="size-3" /> Copy link
                        </Button>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 text-muted-foreground"
                          aria-label="Revoke invitation"
                          onClick={() => revokeInvite.mutate(inv.id)}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="settings-section">
            <div className="settings-section-h">What each role can do</div>
            <div className="role-legend">
              {roles.map((r) => (
                <div className="role-leg" key={r}>
                  <div className="rl-name">{ROLE_LABEL[r] ?? r}</div>
                  <div className="rl-desc">{ROLE_DESC[r]}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function relTime(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function InviteUserDialog({ orgId }: { orgId: string }) {
  const api = useApi();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"org_member" | "org_admin">("org_member");
  const [created, setCreated] = useState<InvitationRow | null>(null);

  const invite = useMutation({
    mutationFn: () =>
      api.request<InvitationRow>(`/admin/orgs/${orgId}/invitations`, {
        method: "POST",
        body: JSON.stringify({ org_id: orgId, email, role, expires_in_days: 14 }),
      }),
    onSuccess: (inv) => {
      void qc.invalidateQueries({ queryKey: ["admin-invitations", orgId] });
      setCreated(inv);
      toast.success("Invitation created");
    },
    onError: (err: { message?: string }) => toast.error(err.message ?? "Invitation failed"),
  });

  const inviteUrl = created ? `${WEB_APP_URL}/invite/${created.token}` : "";

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setEmail("");
          setRole("org_member");
          setCreated(null);
        }
      }}
    >
      <DialogTrigger
        render={
          <Button>
            <Mail className="size-4" /> Invite
          </Button>
        }
      />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{created ? "Invitation sent" : "Invite user"}</DialogTitle>
          <DialogDescription>
            {created
              ? "Share the link with the invitee. We'll also email it if SMTP is configured."
              : "We'll send an email if SMTP is configured. Either way, you'll get a copyable link."}
          </DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-xs">
              <span className="truncate">{created.email}</span>
              <span className="text-muted-foreground">{created.role}</span>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                void navigator.clipboard.writeText(inviteUrl);
                toast.success("Invite link copied");
              }}
            >
              <Copy className="size-4" /> Copy invite link
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                autoFocus
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Role</Label>
              <Select
                value={role}
                onValueChange={(v) => setRole(v as "org_member" | "org_admin")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="org_member">Org Member</SelectItem>
                  <SelectItem value="org_admin">Org Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        <DialogFooter>
          {created ? (
            <Button onClick={() => setOpen(false)}>Done</Button>
          ) : (
            <Button disabled={!email || invite.isPending} onClick={() => invite.mutate()}>
              {invite.isPending ? "Inviting…" : "Send invite"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
